import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
    Eye,
    RefreshCcw,
    Search as SearchIcon,
    Package,
    CreditCard,
    BadgeDollarSign,
    Check,
    X,
    Info,
} from "lucide-react";

import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import { Badge } from "@/components/ui/Badge";

import adminApi from "@/services/api/admin";
import type { Order, RefundRequest } from "@/services/types/domain";
import {
    formatPrice,
    tOrderStatus,
    tPaymentStatus,
    orderVariant,
    paymentVariant,
    tRefundStatus,
    refundVariant,

} from "@/utils/formatters";

const DEFAULT_PER_PAGE = 20;
const DEBOUNCE_MS = 500;

function useDebouncedValue<T>(value: T, delay = DEBOUNCE_MS) {
    const [debounced, setDebounced] = React.useState(value);
    useEffect(() => {
        const id = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(id);
    }, [value, delay]);
    return debounced;
}

const statusOptions = [
    { value: "pending", label: "Pendiente" },
    { value: "processing", label: "En proceso" },
    { value: "shipped", label: "Enviado" },
    { value: "delivered", label: "Entregado" },
    { value: "cancelled", label: "Cancelado" },
    { value: "refunded", label: "Reembolsado" },
];

const paymentStatusOptions = [
    { value: "pending", label: "Pendiente" },
    { value: "paid", label: "Pagado" },
    { value: "failed", label: "Fallido" },
    { value: "refunded", label: "Reembolsado" },
    { value: "partially_refunded", label: "Reemb. parcial" },
];

const sortOptions = [
    { value: "created_desc", label: "Más recientes" },
    { value: "created_asc", label: "Más antiguos" },
    { value: "total_desc", label: "Total más alto" },
    { value: "total_asc", label: "Total más bajo" },
];

type Meta = { current_page: number; per_page: number; total: number; last_page: number };

interface AdminOrdersManagementProps {
    onNavigateBack?: () => void;
}

const AdminOrdersManagementComponent: React.FC<AdminOrdersManagementProps> = ({ onNavigateBack }) => {
    const [params, setParams] = useSearchParams();

    const [rows, setRows] = useState<Order[]>([]);
    const [loading, setLoading] = useState(true);
    const [meta, setMeta] = useState<Meta>({
        current_page: Number(params.get("page") || 1),
        per_page: Number(params.get("per_page") || DEFAULT_PER_PAGE),
        total: 0,
        last_page: 1,
    });

    // filtros (desde URL)
    const page = Number(params.get("page") || 1);
    const per_page = Number(params.get("per_page") || DEFAULT_PER_PAGE);
    const q = params.get("q") || "";
    const status = params.get("status") || "";
    const payment_status = params.get("payment_status") || "";
    const sort = params.get("sort") || "created_desc";

    // búsqueda con debounce
    const [searchDraft, setSearchDraft] = useState(q);
    const debouncedQ = useDebouncedValue(searchDraft);
    useEffect(() => {
        setSearchDraft(q);
    }, [q]);

    // control de carreras
    const latestReq = useRef(0);

    const submitFilters = (next: Partial<Record<string, string | number>>) => {
        const merged = new URLSearchParams(params);
        Object.entries(next).forEach(([k, v]) => {
            if (v === "" || v === undefined || v === null) merged.delete(k);
            else merged.set(k, String(v));
        });
        if (!("page" in next)) merged.set("page", "1");
        setParams(merged, { replace: true });
    };

    // dispara búsqueda tras debounce
    useEffect(() => {
        if (debouncedQ !== q) submitFilters({ q: debouncedQ });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedQ]);

    const fetchData = async () => {
        const reqId = ++latestReq.current;
        try {
            setLoading(true);
            const result = await adminApi.getAllOrders({
                page,
                per_page,
                search: q || undefined,
                status: status || undefined,
                payment_status: payment_status || undefined,
                //sort,
            });
            if (reqId !== latestReq.current) return;
            setRows(result.data as Order[]);
            setMeta(result.meta as Meta);
        } catch (err: any) {
            if (reqId !== latestReq.current) return;
            const msg = err?.response?.data?.message || err?.message || "No se pudieron cargar las órdenes";
            toast.error(msg);
        } finally {
            if (reqId === latestReq.current) setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, per_page, q, status, payment_status, sort]);

    const goPage = (p: number) => {
        if (p < 1 || p > meta.last_page) return;
        submitFilters({ page: p });
    };

    const resetFilters = () => {
        setParams(
            new URLSearchParams({
                page: "1",
                per_page: String(DEFAULT_PER_PAGE),
                sort: "created_desc",
            }),
            { replace: true }
        );
    };

    // estados “busy” locales
    const paymentProcessingIds = useRef<Set<number>>(new Set());
    const statusProcessingIds = useRef<Set<number>>(new Set());
    //const refundProcessingIds = useRef<Set<number>>(new Set());
    const isPaymentBusy = (id: number) => paymentProcessingIds.current.has(id);
    const isStatusBusy = (id: number) => statusProcessingIds.current.has(id);
    //const isRefundBusy = (id: number) => refundProcessingIds.current.has(id);
    const setPaymentBusy = (id: number, busy: boolean) => {
        const s = paymentProcessingIds.current;
        busy ? s.add(id) : s.delete(id);
        setRows((r) => [...r]);
    };
    const setStatusBusy = (id: number, busy: boolean) => {
        const s = statusProcessingIds.current;
        busy ? s.add(id) : s.delete(id);
        setRows((r) => [...r]);
    };
    /*const setRefundBusy = (id: number, busy: boolean) => {
        const s = refundProcessingIds.current;
        busy ? s.add(id) : s.delete(id);
        setRows((r) => [...r]);
    };*/

    // Cambiar estado de PAGO (admin)
    const handleUpdatePaymentStatus = async (
        orderId: number,
        nextPaymentStatus: "pending" | "paid" | "failed" | "refunded" | "partially_refunded"
    ) => {
        setPaymentBusy(orderId, true);
        try {
            await adminApi.updateOrderPaymentStatus(orderId, nextPaymentStatus, "Actualizado por admin");
            setRows((prev: any) =>
                prev.map((o: any) => {
                    if (o.id !== orderId) return o;
                    const wasPendingOrder = o.status === "pending";
                    //const shouldAutoAdvance = wasPendingOrder && updated.payment_status === "paid";
                    const shouldAutoAdvance = wasPendingOrder && nextPaymentStatus === "paid";
                    return {
                        ...o,
                        payment_status: nextPaymentStatus,
                        ...(shouldAutoAdvance ? { status: "processing" as const } : {}),
                        updated_at: new Date().toISOString(),
                    };
                })
            );
            toast.success(`Pago actualizado a ${tPaymentStatus(nextPaymentStatus)}`);

            const row = rows.find((r) => r.id === orderId);
            if (row && row.status === "pending" && nextPaymentStatus === "paid") {
                try {
                    await adminApi.updateOrderStatus(orderId, "processing", "Auto-avance por pago completado");
                } catch {
                    /* noop */
                }
            }
        } catch (error: any) {
            toast.error(error?.response?.data?.message || "No se pudo actualizar el estado de pago");
        } finally {
            setPaymentBusy(orderId, false);
        }
    };

    // Cambiar estado del PEDIDO (no avanza sin pago “paid”)
    const handleUpdateOrderStatus = async (orderId: number, newStatus: Order["status"]) => {
        const current = rows.find((o) => o.id === orderId);
        if (!current) return;
        if (current.payment_status !== "paid" && ["processing", "shipped", "delivered"].includes(newStatus)) {
            toast.error("Debes confirmar el pago (Pagado) antes de avanzar el estado del pedido.");
            return;
        }

        setStatusBusy(orderId, true);
        try {
            await adminApi.updateOrderStatus(orderId, newStatus, "Estado actualizado por admin");
            setRows((prev) =>
                prev.map((o) => (o.id === orderId ? { ...o, status: newStatus, updated_at: new Date().toISOString() } : o))
            );
            toast.success(`Orden actualizada a ${tOrderStatus(newStatus)}`);
        } catch (error: any) {
            toast.error(error?.response?.data?.message || "Error al actualizar estado de la orden");
        } finally {
            setStatusBusy(orderId, false);
        }
    };

    // ---------- NUEVO: Estado del modal con detalle de reembolso ----------
    const [refundDialog, setRefundDialog] = useState<{
        open: boolean;
        mode: "approve" | "reject";
        orderId: number | null;
        note: string;
        amount: string;
        busy: boolean;
        loadingDetails: boolean;
        details: RefundRequest | null;
    }>({
        open: false,
        mode: "approve",
        orderId: null,
        note: "",
        amount: "",
        busy: false,
        loadingDetails: false,
        details: null,
    });

    const selectedOrder = useMemo(
        () => rows.find((r) => r.id === refundDialog.orderId),
        [refundDialog.orderId, rows]
    );

    const openRefundDialog = async (orderId: number, mode: "approve" | "reject") => {
        setRefundDialog({
            open: true,
            mode,
            orderId,
            note: "",
            amount: "",
            busy: false,
            loadingDetails: true,
            details: null,
        });

        try {
            // Opción A: endpoint por ORDER ID (recomendado)
            const details = await adminApi.getRefundRequestForOrder(orderId);

            // Opción B (si tu backend SOLO tiene /refund-requests/:id):
            // - añade refund_request_id en 'rows'
            // - const details = await adminApi.getRefundRequestById(row.refund_request_id)

            setRefundDialog((s) => ({ ...s, loadingDetails: false, details }));
        } catch (err: any) {
            const msg = err?.response?.data?.message || err?.message || "No se pudo cargar la solicitud";
            toast.error(msg);
            setRefundDialog((s) => ({ ...s, loadingDetails: false, details: null }));
        }
    };

    const submitRefundDialog = async () => {
        console.log("refundDialog:", refundDialog);
        //return
        const { orderId, mode, note, amount, details: refund_request } = refundDialog;
        if (!orderId) return;
        if (!refund_request) return
        setRefundDialog((s) => ({ ...s, busy: true }));
        try {
            const refund_amount = amount ? Number(amount) : undefined;
            const decision = mode === "approve" ? "approved" : "rejected";
            //const updated = await adminApi.reviewRefundRequest(orderId, decision, { note, refund_amount });
            await adminApi.updateReviewRefundRequest(refund_request.id, decision, { note, refund_amount });
            const current = rows.find((o) => o.id === orderId);
            if (!current) {
                toast.error("No se encontró la orden en la lista.");
                return;
            }
            let nextPayment = current.payment_status;
            if (decision === "approved") {
                // Heurística: si no tenemos total de orden, caemos a 'refunded'
                /*if (!orderTotal || typeof effectiveAmount !== "number") {
                    nextPayment = "refunded";
                } else {
                    // margen de redondeo para comparar totales
                    const EPS = 0.005;
                    nextPayment = (effectiveAmount >= orderTotal - EPS)
                        ? "refunded"
                        : "partially_refunded";
                }*/
                nextPayment = "refunded";
            } else {
                // rejected → no cambia payment_status
                nextPayment = current.payment_status;
            }
            setRows((prev) =>
                prev.map((o) => {
                    if (o.id !== orderId) return o;
                    return {
                        ...o,
                        payment_status: nextPayment,
                        refund_request_status: decision,
                        updated_at: new Date().toISOString(),
                    };
                })
            );

            toast.success(decision === "approved" ? "Reembolso aprobado" : "Reembolso rechazado");
            setRefundDialog({
                open: false,
                mode: "approve",
                orderId: null,
                note: "",
                amount: "",
                busy: false,
                loadingDetails: false,
                details: null,
            });
        } catch (error: any) {
            toast.error(error?.response?.data?.message || "No se pudo procesar la solicitud de reembolso");
            setRefundDialog((s) => ({ ...s, busy: false }));
        }
    };

    //const empty = !loading && rows.length === 0;

    return (
        <div className="min-h-screen bg-gray-50 px-4 py-6">
            <div className="mx-auto w-full max-w-7xl">
                {/* Header */}
                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Gestión de órdenes</h1>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" onClick={fetchData} leftIcon={<RefreshCcw className="w-4 h-4" />}>
                            Actualizar
                        </Button>
                        {onNavigateBack && (
                            <button
                                onClick={onNavigateBack}
                                className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"
                            >
                                Volver al Dashboard
                            </button>
                        )}
                    </div>
                </div>

                {/* Filtros */}
                <div className="mb-4 grid grid-cols-1 gap-3 rounded-2xl border bg-white p-3 sm:p-4 sm:grid-cols-12">
                    <div className="sm:col-span-4">
                        <Input
                            label="Buscar"
                            placeholder="N° de pedido, email, nombre… (Enter para buscar)"
                            value={searchDraft}
                            onChange={(e) => setSearchDraft(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") submitFilters({ q: searchDraft });
                            }}
                            leftIcon={<SearchIcon className="w-4 h-4" />}
                            fullWidth
                        />
                    </div>
                    <div className="sm:col-span-2">
                        <label className="mb-1 block text-sm font-medium text-gray-700">Estado de pedido</label>
                        <select
                            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            value={status}
                            onChange={(e) => submitFilters({ status: e.target.value })}
                        >
                            <option value="">Todos</option>
                            {statusOptions.map((o) => (
                                <option key={o.value} value={o.value}>
                                    {o.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="sm:col-span-2">
                        <label className="mb-1 block text-sm font-medium text-gray-700">Estado de pago</label>
                        <select
                            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            value={payment_status}
                            onChange={(e) => submitFilters({ payment_status: e.target.value })}
                        >
                            <option value="">Todos</option>
                            {paymentStatusOptions.map((o) => (
                                <option key={o.value} value={o.value}>
                                    {o.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="sm:col-span-2">
                        <label className="mb-1 block text-sm font-medium text-gray-700">Ordenar</label>
                        <select
                            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            value={sort}
                            onChange={(e) => submitFilters({ sort: e.target.value })}
                        >
                            {sortOptions.map((o) => (
                                <option key={o.value} value={o.value}>
                                    {o.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="sm:col-span-2 flex items-end gap-2">
                        <Button variant="outline" onClick={() => submitFilters({ q: searchDraft })}>Buscar</Button>
                        <Button variant="outline" onClick={resetFilters}>Limpiar</Button>
                    </div>
                </div>

                {/* Tabla */}
                <div className="rounded-2xl border bg-white">
                    <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-3 text-xs font-semibold text-gray-500">
                        <div className="col-span-2">Fecha</div>
                        <div className="col-span-2">Pedido</div>
                        <div className="col-span-2">Cliente</div>
                        <div className="col-span-2 text-right">Total</div>
                        <div className="col-span-3">Estados</div>
                        <div className="col-span-1 text-right">Acciones</div>
                    </div>

                    <div className="divide-y">
                        {loading &&
                            Array.from({ length: 6 }).map((_, i) => (
                                <div key={i} className="px-4 py-4 animate-pulse">
                                    <div className="h-6 bg-gray-100 rounded w-2/3" />
                                </div>
                            ))}

                        {!loading && !rows.length && (
                            <div className="px-4 py-8 text-center text-gray-600">No hay órdenes para mostrar.</div>
                        )}

                        {!loading &&
                            rows.map((o) => {
                                return (
                                    <div key={o.id} className="px-4 py-3 grid grid-cols-1 gap-3 md:grid-cols-12 md:items-center">
                                        <div className="md:col-span-2 text-sm text-gray-700">
                                            {o.created_at ? new Date(o.created_at).toLocaleString() : "—"}
                                        </div>
                                        <div className="md:col-span-2">
                                            <div className="text-sm font-semibold text-gray-900">
                                                {o.order_number || `#${o.id}`}
                                            </div>
                                            <div className="text-xs text-gray-500">{o.id}</div>
                                        </div>
                                        <div className="md:col-span-2 text-sm text-gray-700">
                                            {o.first_name || o.last_name ? (
                                                <span>{[o.first_name, o.last_name].filter(Boolean).join(" ")}</span>
                                            ) : (
                                                <span>{o.email || "—"}</span>
                                            )}
                                            {o.email && <div className="text-xs text-gray-500">{o.email}</div>}
                                        </div>
                                        <div className="md:col-span-2 text-right text-sm font-semibold text-gray-900">
                                            {formatPrice(Number(o.total_amount ?? o.subtotal ?? 0), o.currency || "PEN")}
                                        </div>

                                        {/* Estados: Pedido / Pago / (Reembolso solo si existe) */}
                                        <div className="md:col-span-3 flex flex-col gap-2">
                                            {/* Pedido */}
                                            <div className="grid grid-cols-[auto,1fr,auto] items-center gap-2 rounded-xl border bg-gray-50 p-2">
                                                <span className="text-[11px] text-gray-500">Pedido</span>
                                                <Badge variant={orderVariant(o.status)} icon={<Package className="w-3.5 h-3.5" />}>
                                                    {tOrderStatus(o.status)}
                                                </Badge>
                                                <select
                                                    className="h-8 min-w-[140px] ml-auto text-xs border border-gray-300 rounded-md px-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                                    value={o.status}
                                                    onChange={(e) => handleUpdateOrderStatus(o.id, e.target.value as Order["status"])}
                                                    disabled={isStatusBusy(o.id)}
                                                    title="Cambiar estado del pedido"
                                                >
                                                    {statusOptions.map((s) => (
                                                        <option key={s.value} value={s.value}>{s.label}</option>
                                                    ))}
                                                </select>
                                            </div>

                                            {/* Pago */}
                                            <div className="grid grid-cols-[auto,1fr,auto] items-center gap-2 rounded-xl border bg-gray-50 p-2">
                                                <span className="text-[11px] text-gray-500">Pago</span>
                                                <Badge variant={paymentVariant(o.payment_status)} icon={<CreditCard className="w-3.5 h-3.5" />}>
                                                    {tPaymentStatus(o.payment_status)}
                                                </Badge>
                                                <select
                                                    className="h-8 min-w-[160px] ml-auto text-xs border border-gray-300 rounded-md px-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                                    value={o.payment_status}
                                                    onChange={(e) => handleUpdatePaymentStatus(o.id, e.target.value as Order["payment_status"])}
                                                    disabled={isPaymentBusy(o.id)}
                                                    title="Cambiar estado de pago"
                                                >
                                                    {paymentStatusOptions.map((s) => (
                                                        <option key={s.value} value={s.value}>{s.label}</option>
                                                    ))}
                                                </select>
                                            </div>

                                            {/* Reembolso — SOLO si hay estado */}
                                            {!!o.refund_request_status && (
                                                <div className="grid grid-cols-[auto,1fr,auto] items-center gap-2 rounded-xl border bg-gray-50 p-2">
                                                    <span className="text-[11px] text-gray-500">Reembolso</span>
                                                    <Badge variant={refundVariant(o.refund_request_status)} icon={<BadgeDollarSign className="w-3.5 h-3.5" />}>
                                                        {tRefundStatus(o.refund_request_status)}
                                                    </Badge>

                                                    {/* Botones icon-only: solo si está 'pending' */}
                                                    {o.refund_request_status === "pending" ? (
                                                        <div className="ml-auto flex items-center gap-1">
                                                            <Button
                                                                size="xs"
                                                                variant="outline"
                                                                className="p-2"
                                                                aria-label="Aprobar reembolso"
                                                                title="Aprobar reembolso"
                                                                onClick={() => openRefundDialog(o.id, "approve")}
                                                            >
                                                                <Check className="w-4 h-4" />
                                                            </Button>
                                                            <Button
                                                                size="xs"
                                                                variant="outline"
                                                                className="p-2"
                                                                aria-label="Rechazar reembolso"
                                                                title="Rechazar reembolso"
                                                                onClick={() => openRefundDialog(o.id, "reject")}
                                                            >
                                                                <X className="w-4 h-4" />
                                                            </Button>
                                                        </div>
                                                    ) : (
                                                        <div className="ml-auto" />
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        {/* Acciones */}
                                        <div className="md:col-span-1 flex md:justify-end">
                                            <Button asChild size="sm" variant="outline" title="Ver detalle">
                                                <Link to={`/admin/orders/${o.id}`}>
                                                    <Eye className="w-4 h-4" />
                                                </Link>
                                            </Button>
                                        </div>
                                    </div>
                                );
                            })}
                    </div>
                </div>

                {/* Paginación */}
                <div className="mt-6 flex items-center justify-center gap-3">
                    <Button variant="outline" size="sm" onClick={() => goPage(meta.current_page - 1)} disabled={meta.current_page <= 1}>
                        Anterior
                    </Button>
                    <span className="text-sm text-gray-700">
                        Página <strong>{meta.current_page}</strong> de <strong>{meta.last_page}</strong>
                    </span>
                    <Button variant="outline" size="sm" onClick={() => goPage(meta.current_page + 1)} disabled={meta.current_page >= meta.last_page}>
                        Siguiente
                    </Button>
                </div>
            </div>

            {/* Diálogo: Aprobar / Rechazar reembolso (con DETALLE) */}
            {refundDialog.open && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center">
                    <div className="absolute inset-0 bg-black/30" onClick={() => setRefundDialog((s) => ({ ...s, open: false }))} />
                    <div className="relative z-[61] w-full max-w-lg rounded-2xl border bg-white p-5 shadow-xl">
                        <div className="flex items-center gap-2 mb-2">
                            <Info className="w-5 h-5 text-primary-600" />
                            <h3 className="text-lg font-semibold text-gray-900">
                                {refundDialog.mode === "approve" ? "Aprobar reembolso" : "Rechazar reembolso"}
                            </h3>
                        </div>

                        {/* Detalle de la solicitud */}
                        <div className="rounded-xl border bg-gray-50 p-3">
                            {refundDialog.loadingDetails && <div className="text-sm text-gray-600">Cargando solicitud…</div>}

                            {!refundDialog.loadingDetails && refundDialog.details && (
                                <div className="space-y-2 text-sm">
                                    <div className="flex items-center justify-between">
                                        <div className="text-gray-600">Pedido:</div>
                                        <div className="font-medium">{refundDialog.details.order_number}</div>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <div className="text-gray-600">Comprador:</div>
                                        <div className="font-medium">
                                            {[refundDialog.details.first_name, refundDialog.details.last_name].filter(Boolean).join(" ")} ({refundDialog.details.user_email})
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-gray-600">Motivo:</div>
                                        <div className="mt-1 rounded-md bg-white p-2 text-gray-800 border">{refundDialog.details.reason || "—"}</div>
                                    </div>

                                    <div>
                                        <div className="text-gray-600 mb-1">Artículos solicitados:</div>
                                        <div className="rounded-md bg-white border divide-y">
                                            {refundDialog.details.items.map((it, idx) => (
                                                <div key={idx} className="px-3 py-2 flex items-center justify-between gap-3">
                                                    <div className="min-w-0">
                                                        <div className="font-medium text-gray-900 truncate">{it.product_name}</div>
                                                        <div className="text-xs text-gray-500">SKU: {it.product_sku || "—"}</div>
                                                    </div>
                                                    <div className="text-right">
                                                        <div className="text-xs text-gray-500">Cant: {it.quantity}</div>
                                                        <div className="text-xs text-gray-500">
                                                            Total:{" "}
                                                            <span className="font-medium">
                                                                {formatPrice(Number(it.total ?? 0), selectedOrder?.currency || "PEN")}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Total sugerido: suma de items (si quisieras mostrarlo) */}
                                    {/* 
                  <div className="flex items-center justify-between pt-1">
                    <span className="text-gray-600">Total solicitado:</span>
                    <span className="font-semibold">
                      {formatPrice(
                        refundDialog.details.items.reduce((acc, it) => acc + Number(it.total ?? 0), 0),
                        selectedOrder?.currency || "PEN"
                      )}
                    </span>
                  </div>
                  */}
                                </div>
                            )}

                            {!refundDialog.loadingDetails && !refundDialog.details && (
                                <div className="text-sm text-gray-600">No se encontró detalle de la solicitud.</div>
                            )}
                        </div>

                        {/* Campos para aprobar/rechazar */}
                        {refundDialog.mode === "approve" && (
                            <div className="mt-3" hidden>
                                <label className="text-sm text-gray-700">Monto a reembolsar (opcional)</label>
                                <input
                                    type="number"
                                    min={0}
                                    step="0.01"
                                    className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                    value={refundDialog.amount}
                                    onChange={(e) => setRefundDialog((s) => ({ ...s, amount: e.target.value }))}
                                    placeholder="0.00"
                                />
                            </div>
                        )}

                        <div className="mt-3" hidden>
                            <label className="text-sm text-gray-700">Nota para el comprador (opcional)</label>
                            <textarea
                                className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                rows={3}
                                value={refundDialog.note}
                                onChange={(e) => setRefundDialog((s) => ({ ...s, note: e.target.value }))}
                                placeholder={refundDialog.mode === "approve" ? "Ej. reembolso total" : "Ej. no cumple política de reembolso"}
                            />
                        </div>

                        <div className="mt-5 flex items-center justify-end gap-2">
                            <Button variant="outline" onClick={() => setRefundDialog((s) => ({ ...s, open: false }))} disabled={refundDialog.busy}>
                                Cerrar
                            </Button>
                            <Button onClick={submitRefundDialog} disabled={refundDialog.busy}>
                                {refundDialog.busy ? "Procesando…" : refundDialog.mode === "approve" ? "Aprobar" : "Rechazar"}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminOrdersManagementComponent;
